<template>
  <div>
    <b-modal
      v-if="showUploadStrengthDataCSV"
      v-model="showUploadStrengthDataCSV"
      size="lg"
      :hide-footer="Boolean(true)"
      @hide="resetGPSUploadModal"
    >
      <div>
        <h2>Import CSV file</h2>
        <hr />

        <div class="type-of-csv-import">
          <h3>Upload Physical Performance Tests</h3>
          <div class="example-template" v-if="players && players.length">
            <a @click="downloadSquadTemplate()">
              <i class="bi bi-people-fill" aria-hidden="true"></i> Download this
              squad's template ({{ players.length }}
              {{ players.length === 1 ? "player" : "players" }}, IDs
              pre-filled)</a
            >
          </div>
          <div class="example-template">
            <a @click="downloadCSVData()" target="_blank">
              <i class="bi bi-file-spreadsheet" aria-hidden="true"></i> Download
              the blank example template</a
            >
          </div>
        </div>

        <div
          class="upload-wrapper"
          v-if="gpsUploadDataTypeSelected !== undefined"
        >
          <label
            >File
            <input type="file" @change="importCSV($event)" />
          </label>

          <div
            v-if="validation !== null"
            class="validation-errors days-red-warning"
          >
            <h3>Template errors</h3>
            <p>
              <i class="bi bi-exclamation-circle" aria-hidden="true"></i>
              {{ validation }}
            </p>
          </div>

          <div
            v-if="csvDataImported !== null && validation === null"
            class="rows-ready"
          >
            <i class="bi bi-check2-circle" aria-hidden="true"></i>
            {{ csvDataImported.length }}
            {{ csvDataImported.length === 1 ? "row" : "rows" }} ready to import.
          </div>

          <div
            v-if="importResult !== null"
            class="import-result"
            :class="{ 'days-red-warning': importResult.errorCount > 0 }"
          >
            <h3>Import summary</h3>
            <p>
              Imported <strong>{{ importResult.inserted }}</strong
              >, updated <strong>{{ importResult.updated }}</strong
              >, skipped
              <strong>{{ importResult.skipped }}</strong> duplicate(s).
            </p>
            <template v-if="importResult.errorCount > 0">
              <p>
                <i class="bi bi-exclamation-circle" aria-hidden="true"></i>
                {{ importResult.errorCount }} row(s) had problems and were not
                imported:
              </p>
              <ul class="import-error-list">
                <li v-for="(err, idx) in importResult.errors" :key="idx">
                  {{ err }}
                </li>
              </ul>
            </template>
          </div>

          <label
            v-if="csvDataImported !== null && validation === null"
            class="overwrite-toggle"
          >
            <input type="checkbox" v-model="overwriteExisting" />
            Overwrite existing results when a player &amp; date already has data
            <span class="overwrite-hint"
              >(otherwise duplicates are skipped)</span
            >
          </label>

          <button
            v-if="csvDataImported !== null && validation === null"
            :disabled="saving"
            v-on:click="importUploadedCSVDataFile()"
          >
            {{ saving ? "Saving…" : "Save" }}
          </button>
        </div>
        <p class="days-red-warning" v-if="dataCSVErrorMessage !== null">
          <i class="bi bi-exclamation-circle" aria-hidden="true"></i>
          {{ dataCSVErrorMessage }}
        </p>
      </div>
    </b-modal>
  </div>
</template>
<script>
import { PlayerPhysicalPerformance } from "@/services/player-physical-performance-service.js";
export default {
  name: "StrengthTestUploader",
  emits: ["close", "dataUploaded", "update:openStrengthTestModalCall"],
  data() {
    return {
      showUploadStrengthDataCSV: false,
      gpsUploadDataTypeSelected: "",
      dataCSVErrorMessage: null,
      csvDataImported: null,
      validation: null,
      importResult: null,
      saving: false,
      overwriteExisting: false,
    };
  },
  props: {
    openStrengthTestModal: {
      type: Boolean,
      default: false,
    },
    openStrengthTestModalCall: {
      type: Boolean,
      default: false,
    },
    gameTrainingData: {
      type: Object,
      default: () => {},
    },
    // The selected team's players (each has id + name) used to pre-fill the
    // squad CSV template so admins don't have to look up player IDs.
    players: {
      type: Array,
      default: () => [],
    },
  },
  watch: {
    openStrengthTestModal: function (newVal) {
      this.showUploadStrengthDataCSV = newVal;
    },
    openStrengthTestModalCall: function (newVal) {
      this.showUploadStrengthDataCSV = newVal;
    },
  },
  created() {},
  methods: {
    // Canonical header row. The metric columns map 1:1 to what the backend
    // importer expects; "Name" is only there to help the admin eyeball rows and
    // is ignored on import.
    csvHeaderRow() {
      return "Name,player_id,Date,Countermovement Jump (cm),Reactive Strength Index,10m Sprint (s),30m Sprint (s),Fitness Test (30-15),Standing Height (cm),Weight (kg)";
    },
    // Quote a CSV field if it contains a comma, quote or newline.
    csvEscape(value) {
      const s = (value ?? "").toString();
      return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
    },
    triggerCSVDownload(csv, filename) {
      const anchor = document.createElement("a");
      anchor.href = "data:text/csv;charset=utf-8," + encodeURIComponent(csv);
      anchor.target = "_blank";
      anchor.download = filename;
      anchor.click();
    },
    downloadCSVData() {
      // Blank template: header + one realistic, column-aligned example row.
      const example =
        "Jordan Example,1234,10/12/2024,32.5,1.8,1.85,4.20,18.5,175,68";
      this.triggerCSVDownload(
        this.csvHeaderRow() + "\n" + example,
        "example-strength-Test.csv",
      );
    },
    downloadSquadTemplate() {
      // Pre-fill one row per player with their Name + player_id; the admin fills
      // in the Date and metric columns. Ten columns to match the header.
      const rows = this.players.map((p) =>
        [
          this.csvEscape(p.name),
          this.csvEscape(p.id),
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
        ].join(","),
      );
      const csv = [this.csvHeaderRow(), ...rows].join("\n");
      this.triggerCSVDownload(csv, "squad-physical-performance-template.csv");
    },
    // Minimal RFC-4180-ish parser: handles quoted fields, embedded commas,
    // escaped quotes ("") and CRLF/LF line endings. Returns rows of strings.
    parseCSV(text) {
      const rows = [];
      let row = [];
      let field = "";
      let inQuotes = false;
      for (let i = 0; i < text.length; i++) {
        const c = text[i];
        if (inQuotes) {
          if (c === '"') {
            if (text[i + 1] === '"') {
              field += '"';
              i++;
            } else {
              inQuotes = false;
            }
          } else {
            field += c;
          }
        } else if (c === '"') {
          inQuotes = true;
        } else if (c === ",") {
          row.push(field);
          field = "";
        } else if (c === "\n") {
          row.push(field);
          rows.push(row);
          row = [];
          field = "";
        } else if (c !== "\r") {
          field += c;
        }
      }
      if (field !== "" || row.length > 0) {
        row.push(field);
        rows.push(row);
      }
      return rows;
    },
    async importCSV(e) {
      const file = e.target.files.item(0);
      if (!file) return;

      this.validation = null;
      this.importResult = null;
      this.csvDataImported = null;

      const text = await file.text();
      const rows = this.parseCSV(text).filter((r) =>
        r.some((cell) => cell.trim() !== ""),
      );
      if (rows.length < 2) {
        this.validation = "The file has a header but no data rows.";
        return;
      }

      const headers = rows[0].map((h) => h.trim());
      const result = [];
      for (let i = 1; i < rows.length; i++) {
        const obj = {};
        for (let j = 0; j < headers.length; j++) {
          obj[headers[j]] = (rows[i][j] ?? "").trim();
        }
        result.push(obj);
      }

      this.checkTemplateIsCorrect(result);
      this.csvDataImported = result;
    },
    checkTemplateIsCorrect(results) {
      const requiredHeadings = [
        "10m Sprint (s)",
        "30m Sprint (s)",
        "Countermovement Jump (cm)",
        "Date",
        "Reactive Strength Index",
        "Standing Height (cm)",
        "Weight (kg)",
        "player_id",
      ];

      // Validate input
      if (!Array.isArray(results) || results.length === 0) {
        console.warn("No results found or invalid data format.");
        this.validation = "No results found or invalid data format.";
        return false;
      }

      const object = results[0];

      const has = (h) => Object.prototype.hasOwnProperty.call(object, h);
      const missingHeadings = requiredHeadings.filter(
        (heading) => !has(heading),
      );

      // Fitness column accepts the current label or the legacy "(min:sec)" one.
      if (!has("Fitness Test (30-15)") && !has("Fitness Test Time (min:sec)")) {
        missingHeadings.push("Fitness Test (30-15)");
      }

      if (missingHeadings.length > 0) {
        console.warn("Missing fields:", missingHeadings.join(", "));
        this.validation =
          "Missing fields: " + missingHeadings.join(", ").toString();
        return false;
      }

      return true;
    },

    async importUploadedCSVDataFile() {
      this.saveCSVStrengthTestData();
    },
    checkCSVdata() {
      return Object.prototype.hasOwnProperty.call(
        this.csvDataImported[0],
        "Countermovement Jump (cm)",
      );
    },
    csvErrorMessage() {
      this.dataCSVErrorMessage =
        "Please verify that your CSV upload template is correct and that you have selected the appropriate upload option.";
      setTimeout(() => (this.dataCSVErrorMessage = null), 2000);
    },
    convertHeightToCM(data) {
      this.csvDataImported = data.map((item) => {
        let heightStr = item["Standing Height (ft)"]?.trim(); // Handle missing data safely

        if (!heightStr || !heightStr.includes("'")) return item; // Skip if the format is incorrect

        const parts = heightStr.split("'");
        const feet = parseInt(parts[0], 10) || 0;
        const inches = parseInt(parts[1], 10) || 0;

        const heightCM = feet * 30.48 + inches * 2.54;

        return {
          ...item,
          "Standing Height (ft)": Math.round(heightCM).toString(), // Store result in a new key
        };
      });
    },
    async saveCSVStrengthTestData() {
      const dataIsGood = this.checkCSVdata();
      if (dataIsGood !== true) {
        this.csvErrorMessage();
        return;
      }

      const profile = this.$store.getters.getProfile;
      const data = {
        action: "importUploadedStrenghtTestCSVDataFile",
        data: this.csvDataImported,
        // Audit trail: who ran the import.
        created_by: profile ? profile.id : null,
        // When true, duplicates (same player + date) are updated, not skipped.
        overwrite: this.overwriteExisting,
      };

      this.saving = true;
      const responce = await PlayerPhysicalPerformance.find(data).catch(
        (error) => {
          console.error("Strength CSV import failed:", error);
          return null;
        },
      );
      this.saving = false;

      if (!responce || responce.status !== 200) {
        this.$toast.error(
          "Could not save the CSV. Please check your connection and try again.",
        );
        return;
      }

      // Backend returns { inserted, skipped, errorCount, errors[] }.
      const res = responce.data || {};
      this.importResult = {
        inserted: res.inserted ?? 0,
        updated: res.updated ?? 0,
        skipped: res.skipped ?? 0,
        errorCount: res.errorCount ?? 0,
        errors: Array.isArray(res.errors) ? res.errors : [],
      };

      this.$emit("dataUploaded", this.csvDataImported);

      const tally = `Imported ${this.importResult.inserted}, updated ${this.importResult.updated}, skipped ${this.importResult.skipped} duplicate(s).`;

      if (this.importResult.errorCount > 0) {
        // Keep the modal open so the admin can read which rows failed and fix
        // them; the good rows have already been imported.
        this.$toast.warning(
          `${tally} ${this.importResult.errorCount} row(s) had problems.`,
        );
        return;
      }

      this.$toast.success(tally);
      this.showUploadStrengthDataCSV = false;
      this.$emit("update:openStrengthTestModalCall", false);
      this.$emit("close");
    },
    resetGPSUploadModal() {
      // Reset component state
      this.gpsUploadDataTypeSelected = "";
      this.dataCSVErrorMessage = null;
      this.csvDataImported = null;
      this.validation = null;
      this.importResult = null;
      this.saving = false;
      this.overwriteExisting = false;

      // Emit close events to parent
      this.$emit("update:openStrengthTestModalCall", false);
      this.$emit("close");
    },
    fixDateForImport(date) {
      return date.substring(0, 10);
    },
  },
};
</script>
<style scoped lang="scss">
.rows-ready {
  margin: 0.75rem 0;
  font-size: 0.9rem;
  color: #1a8a4f;
  font-weight: 600;
}

.import-result {
  margin: 1rem 0;
  padding: 0.85rem 1rem;
  background: #f3f8f4;
  border: 1px solid #cfe6d6;
  border-radius: 8px;

  &.days-red-warning {
    background: #fdf3f2;
    border-color: #f3d2cd;
  }

  h3 {
    font-size: 1rem;
    margin: 0 0 0.4rem;
  }

  p {
    margin: 0 0 0.4rem;
  }
}

.import-error-list {
  margin: 0.25rem 0 0;
  padding-left: 1.1rem;
  max-height: 220px;
  overflow-y: auto;
  font-size: 0.85rem;

  li {
    margin-bottom: 0.2rem;
  }
}

.overwrite-toggle {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0.75rem 0;
  font-size: 0.9rem;
  cursor: pointer;

  input {
    width: auto;
    margin: 0;
  }

  .overwrite-hint {
    color: #6b7280;
    font-size: 0.8rem;
  }
}

.example-template {
  border: 1px solid #d3d1d1;
  background: #f2f1f1;
  margin-bottom: 1rem;

  a {
    cursor: pointer;
    padding: 1rem;
    display: block;

    &:focus,
    &:hover {
      background: #abdeb6;
    }
  }
}
</style>
